import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowRight, Home as HomeIcon, CloudRain, Paintbrush } from "lucide-react";
import { getArea, images, enabledServices, site } from "@/config/site";
import { SEO, breadcrumbLd, faqLd } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";
import { FAQAccordion } from "@/components/FAQAccordion";
import NotFound from "@/pages/NotFound";

export default function AreaDetail() {
  const { slug } = useParams();
  const area = getArea(slug);
  if (!area) return <NotFound />;
  const img = images[area.image];

  return (
    <>
      <SEO
        title={`${area.title} | North Ember Painting`}
        description={`Professional house painters in ${area.name}, BC. Interior, exterior, cabinet and commercial painting by a fully insured local company with ${site.yearsExperience} years of experience. Free estimates.`}
        path={`/service-areas/${area.slug}`}
        image={img.url}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Service Areas", path: "/service-areas" },
            { name: area.name, path: `/service-areas/${area.slug}` },
          ]),
          faqLd(area.faqs),
        ]}
      />

      <section className="relative overflow-hidden bg-ink" data-testid="area-hero">
        <img src={img.url} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <Reveal>
            <nav aria-label="Breadcrumb" className="text-xs font-medium uppercase tracking-widest text-slate-400">
              <Link to="/" className="hover:text-ember">Home</Link> / <Link to="/service-areas" className="hover:text-ember">Service Areas</Link> /{" "}
              <span className="text-ember">{area.name}</span>
            </nav>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl">
              {area.title}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Fully insured, {site.yearsExperience} years of experience, and based just around the corner.
            </p>
            <div className="mt-8">
              <Link
                to="/estimate"
                data-testid="area-estimate-btn"
                className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-ember px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark"
              >
                Get a Free Estimate in {area.name} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-3">
            <div className="space-y-14 lg:col-span-2">
              <Reveal>
                <div className="space-y-5 text-base leading-relaxed text-slate-600">
                  {area.intro.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <p className="mt-4 text-xs italic text-slate-400">Photography on this page is concept imagery, shown for illustration.</p>
              </Reveal>

              <Reveal>
                <h2 className="flex items-center gap-3 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  <HomeIcon className="h-6 w-6 text-ember" aria-hidden="true" /> Neighbourhoods we cover in {area.name}
                </h2>
                <ul className="mt-6 flex flex-wrap gap-2.5">
                  {area.neighbourhoods.map((n) => (
                    <li key={n} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
                      {n}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal>
                <h2 className="flex items-center gap-3 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  <CloudRain className="h-6 w-6 text-ember" aria-hidden="true" /> Exterior painting in {area.name}'s climate
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{area.exteriorNote}</p>
              </Reveal>

              <Reveal>
                <h2 className="flex items-center gap-3 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  <Paintbrush className="h-6 w-6 text-ember" aria-hidden="true" /> Painting services in {area.name}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{area.servicesNote}</p>
                <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {enabledServices.slice(0, 6).map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/services/${s.slug}`}
                        data-testid={`area-service-${s.slug}`}
                        className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-700 shadow-sm transition-[border-color,color] duration-200 hover:border-ember/40 hover:text-ember-dark"
                      >
                        {s.name}
                        <ArrowRight className="h-4 w-4 text-slate-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-ember" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  {area.name} painting FAQs
                </h2>
                <div className="mt-4">
                  <FAQAccordion faqs={area.faqs} idPrefix={`area-${area.slug}`} />
                </div>
              </Reveal>
            </div>

            <aside className="lg:sticky lg:top-24 lg:self-start">
              <Reveal>
                <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-lg">
                  <h2 className="font-display text-xl font-semibold text-slate-900">Book a free estimate in {area.name}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Tell us about your project — we'll review it and get in touch. {site.hoursShort}.
                  </p>
                  <Link
                    to="/estimate"
                    data-testid="area-sidebar-estimate"
                    className="mt-5 flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark"
                  >
                    Request an Estimate <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a
                    href={site.phoneHref}
                    data-testid="area-sidebar-phone"
                    className="mt-3 flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:border-ember hover:text-ember-dark"
                  >
                    Call {site.phone}
                  </a>
                  <p className="mt-5 flex items-start gap-2 border-t border-slate-100 pt-5 text-xs leading-relaxed text-slate-500">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
                    {site.showAddress
                      ? `${site.address.street}, ${site.address.city}, ${site.address.province} ${site.address.postal}`
                      : `Serving ${area.name} and the South Fraser region`}
                  </p>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>

      <CTA title={`Ready to refresh your ${area.name} property?`} />
    </>
  );
}
